import { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  getDashboardUrl,
  getInvoiceUrl,
  Invoice,
  InvoiceDocumentType,
  InvoiceStatus,
  useIsAdmin,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { MenuItem, DotsMenu, InvoiceStatusChip } from 'components';
import classNames from 'classnames';
import { toEuros } from '@app/i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.grayBackground,
  },
  name: {
    ...theme.fonts.label,
  },
  status: {},
  link: {
    color: theme.colors.blueAccessible,
    textDecoration: 'underline',
  },
  date: {
    ...theme.fonts.caption,
  },
  spacer: {
    flex: 1,
  },
}));

interface Props {
  invoice: Invoice;
}

export const InvoiceLine = ({ invoice }: Props) => {
  const classes = useStyles();
  const { i18n, t } = useTranslation();

  const dateFormat = Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const invoiceUrl = getInvoiceUrl(invoice);
  const pdfUrl = getInvoiceUrl(invoice, InvoiceDocumentType.PDF);
  const creditNoteUrl = getInvoiceUrl(invoice, InvoiceDocumentType.CREDIT_NOTE);
  const isAdmin = useIsAdmin();

  const onPdfClick = useCallback(() => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener noreferrer');
    }
  }, [pdfUrl]);

  const onUrlClick = useCallback(() => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener noreferrer');
    }
  }, [invoiceUrl]);

  const onDashboardClick = useCallback(() => {
    const url = getDashboardUrl(invoice);
    if (url) {
      window.open(url, '_blank', 'noopener noreferrer');
    }
  }, [invoice]);

  const onCreditNoteClick = useCallback(() => {
    if (creditNoteUrl) {
      window.open(creditNoteUrl, '_blank', 'noopener noreferrer');
    }
  }, [creditNoteUrl]);

  return (
    <div className={classes.container}>
      {invoice.status !== InvoiceStatus.DRAFT ? (
        <a
          href={invoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classNames(classes.name, classes.link)}
        >
          {invoice.name}
        </a>
      ) : (
        <div className={classes.name}>{invoice.name}</div>
      )}
      <div className={classes.name}>
        {invoice.total !== undefined ? toEuros(invoice.total) : ''}
      </div>
      <div className={classes.status}>
        <InvoiceStatusChip status={invoice.status} />
      </div>
      <div className={classes.spacer} />
      <div className={classes.date}>
        {dateFormat.format(
          moment(
            invoice.openAt || invoice.generatedAt || invoice.createdAt,
          ).toDate(),
        )}
      </div>
      <div>
        <DotsMenu horizontal>
          {isAdmin && getDashboardUrl(invoice) && (
            <MenuItem onClick={onDashboardClick}>
              {t('billing.invoice.view')}
            </MenuItem>
          )}
          {invoice.status !== InvoiceStatus.DRAFT && (
            <MenuItem onClick={onUrlClick}>
              {t('billing.invoice.view')}
            </MenuItem>
          )}
          <MenuItem onClick={onPdfClick}>
            {t('billing.invoice.download')}
          </MenuItem>
          {!!invoice.creditNoteId && (
            <MenuItem onClick={onCreditNoteClick}>
              {t('billing.invoice.creditNote')}
            </MenuItem>
          )}
        </DotsMenu>
      </div>
    </div>
  );
};
